import { isAcceptableToDrop } from "@circle-react-shared/BuilderUI/Helpers/droppableAcceptsDraggable";
import { MAX_TREE_DEPTH } from "../constants";
import type { BlockMetadata, TreeItem } from "../types";
import { TreeOperations } from "./TreeOperations";

export const ValidationOperations = {
  extractAcceptsRejects: (metaProps: Record<string, any>) => {
    const accepts: string[] = [];
    const rejects: string[] = [];

    for (const propDef of Object.values(metaProps)) {
      if (propDef.prop === "children" && propDef.type === "children") {
        if (propDef.accepts) accepts.push(...propDef.accepts);
        if (propDef.rejects) rejects.push(...propDef.rejects);
      }
    }

    return { accepts, rejects };
  },

  canDropAtRoot: (draggedMetadata: BlockMetadata, rootMeta: any): boolean => {
    const childrenProp = rootMeta?.props?.children;
    if (!childrenProp) return true;

    return isAcceptableToDrop({
      accepts: childrenProp.accepts || [],
      categories: [...draggedMetadata.category] as any,
      rejects: childrenProp.rejects || [],
    });
  },

  canDropInPropertyNode: (
    draggedMetadata: BlockMetadata,
    targetParentItem: TreeItem,
    getPropertyNodeMetadata: (
      parentId: string,
      propName: string,
    ) => { accepts: string[]; rejects: string[] } | null,
  ): boolean => {
    const parsed = TreeOperations.parsePropertyNodeId(targetParentItem.id);
    if (!parsed) return false;

    const propMetadata = getPropertyNodeMetadata(
      parsed.parentBlockId,
      parsed.propName,
    );
    if (!propMetadata) return false;

    return isAcceptableToDrop({
      accepts: propMetadata.accepts as any,
      categories: [...draggedMetadata.category] as any,
      rejects: propMetadata.rejects as any,
    });
  },

  canDropInRegularBlock: (
    draggedMetadata: BlockMetadata,
    targetParentItem: TreeItem,
    getBlockMetadata: (blockId: string) => BlockMetadata | null,
  ): boolean => {
    const targetBlockId = TreeOperations.getActualBlockId(targetParentItem.id);
    const targetMetadata = getBlockMetadata(targetBlockId);

    if (!targetMetadata) return false;

    if (targetMetadata.metaProps) {
      for (const prop of Object.values(targetMetadata.metaProps)) {
        if (prop.type === "children") return false;
      }
    }

    return isAcceptableToDrop({
      accepts: targetMetadata.accepts as any,
      categories: [...draggedMetadata.category] as any,
      rejects: targetMetadata.rejects as any,
    });
  },

  wouldCreateCircularReference: (
    draggedId: string,
    targetParentId: string | null,
    items: TreeItem[],
  ): boolean =>
    !targetParentId
      ? false
      : draggedId === targetParentId
        ? true
        : TreeOperations.findParentIds(items, targetParentId).has(draggedId),

  checkForCircularReferences: (
    newItems: TreeItem[],
    items: TreeItem[],
  ): boolean => {
    const stack: { item: TreeItem; depth: number }[] = newItems.map(item => ({
      item,
      depth: 0,
    }));

    while (stack.length) {
      const current = stack.pop();
      if (!current) continue;

      const { item: currentItem, depth } = current;

      if (depth > MAX_TREE_DEPTH) return true;

      const parent = TreeOperations.findParentItem(newItems, currentItem.id);
      if (
        parent &&
        ValidationOperations.wouldCreateCircularReference(
          currentItem.id,
          parent.id,
          items,
        )
      ) {
        return true;
      }

      if (currentItem.children) {
        for (const child of currentItem.children) {
          stack.push({ item: child, depth: depth + 1 });
        }
      }
    }
    return false;
  },

  validateItemAndChildren: (
    item: TreeItem,
    parentItem: TreeItem | null,
    originalItems: TreeItem[],
    newItems: TreeItem[],
    canDropBlock: (
      draggedItem: TreeItem,
      targetParentItem: TreeItem | null,
    ) => boolean,
  ): boolean => {
    const validateItemMove = (
      item: TreeItem,
      parentItem: TreeItem | null,
      originalItems: TreeItem[],
    ): boolean => {
      const originalParent = TreeOperations.findParentItem(
        originalItems,
        item.id,
      );
      return (
        originalParent?.id === parentItem?.id || canDropBlock(item, parentItem)
      );
    };

    if (!item.children?.length) {
      return (
        TreeOperations.isPropertyNode(item) ||
        validateItemMove(item, parentItem, originalItems)
      );
    }

    if (TreeOperations.isPropertyNode(item)) {
      return item.children.every(child =>
        ValidationOperations.validateItemAndChildren(
          child,
          item,
          originalItems,
          newItems,
          canDropBlock,
        ),
      );
    }

    if (!validateItemMove(item, parentItem, originalItems)) {
      return false;
    }

    return item.children.every(child =>
      ValidationOperations.validateItemAndChildren(
        child,
        item,
        originalItems,
        newItems,
        canDropBlock,
      ),
    );
  },
} as const;
